import React, { useEffect, useRef } from 'react';

interface Cloud {
  x: number;
  y: number;
  scale: number;
  speed: number;
  opacity: number;
}

interface Star {
  x: number;
  y: number;
  size: number;
  pulseSpeed: number;
  offset: number;
}

interface Gem {
  baseX: number;
  baseY: number;
  amplitudeX: number;
  amplitudeY: number;
  speed: number;
  offset: number;
  rotation: number;
  rotationSpeed: number;
  size: number;
  hue: number;
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  length: number;
  life: number;
  maxLife: number;
}

// Classic rainbow band colors (outer → inner) with gentle alpha
const RAINBOW_BANDS = [
  'rgba(244, 114, 182, 0.45)', // pink
  'rgba(248, 113, 113, 0.45)', // red
  'rgba(251, 146, 60, 0.45)',  // orange
  'rgba(250, 204, 21, 0.45)',  // yellow
  'rgba(74, 222, 128, 0.45)',  // green
  'rgba(96, 165, 250, 0.45)',  // blue
  'rgba(167, 139, 250, 0.45)', // violet
];

const RainbowBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let clouds: Cloud[] = [];
    let stars: Star[] = [];
    let gems: Gem[] = [];
    let shootingStars: ShootingStar[] = [];
    let nextShootingStarAt = 2;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) renderStatic();
    };

    function initScene() {
      clouds = Array.from({ length: 6 }, () => ({
        x: Math.random() * canvas.width,
        y: canvas.height * (0.05 + Math.random() * 0.35),
        scale: 0.7 + Math.random() * 0.8,
        speed: 0.15 + Math.random() * 0.25,
        opacity: 0.55 + Math.random() * 0.3,
      }));

      stars = Array.from({ length: 28 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height * 0.6,
        size: 1 + Math.random() * 1.8,
        pulseSpeed: 0.8 + Math.random() * 1.6,
        offset: Math.random() * Math.PI * 2,
      }));

      gems = Array.from({ length: 14 }, () => ({
        baseX: Math.random() * canvas.width,
        baseY: canvas.height * (0.2 + Math.random() * 0.6),
        amplitudeX: 30 + Math.random() * 60,
        amplitudeY: 20 + Math.random() * 50,
        speed: 0.25 + Math.random() * 0.5,
        offset: Math.random() * Math.PI * 2,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        size: 4 + Math.random() * 5,
        hue: 280 + Math.random() * 80, // pink → violet
      }));

      shootingStars = [];
      nextShootingStarAt = 2 + Math.random() * 4;
    }

    let time = 0;

    function drawSky() {
      const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
      sky.addColorStop(0, 'rgba(255, 228, 245, 1)');   // light pink
      sky.addColorStop(0.45, 'rgba(245, 215, 255, 1)'); // lavender
      sky.addColorStop(1, 'rgba(220, 200, 255, 1)');    // soft violet
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawRainbowArc() {
      // Arc anchored below the screen — only the top part visible
      const cx = canvas.width / 2;
      const cy = canvas.height * 1.05;
      const baseRadius = Math.max(canvas.width, canvas.height) * 0.85;
      // Gentle vertical drift
      const drift = Math.sin(time * 0.3) * 6;

      ctx.save();
      ctx.lineCap = 'round';
      RAINBOW_BANDS.forEach((color, i) => {
        const r = baseRadius - i * 22;
        ctx.beginPath();
        ctx.arc(cx, cy + drift, r, Math.PI, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 24;
        ctx.shadowColor = color;
        ctx.shadowBlur = 18;
        ctx.stroke();
      });
      ctx.restore();
    }

    function drawCloud(c: Cloud) {
      ctx.save();
      ctx.globalAlpha = c.opacity;
      const s = c.scale;
      // Cluster of soft white circles
      const puffs = [
        { dx: 0, dy: 0, r: 28 * s },
        { dx: 26 * s, dy: -6 * s, r: 22 * s },
        { dx: -28 * s, dy: -4 * s, r: 24 * s },
        { dx: 12 * s, dy: 10 * s, r: 20 * s },
        { dx: -14 * s, dy: 12 * s, r: 18 * s },
      ];
      puffs.forEach((p) => {
        const grad = ctx.createRadialGradient(
          c.x + p.dx,
          c.y + p.dy,
          0,
          c.x + p.dx,
          c.y + p.dy,
          p.r
        );
        grad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
        grad.addColorStop(0.7, 'rgba(255, 240, 250, 0.6)');
        grad.addColorStop(1, 'rgba(255, 220, 245, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(c.x + p.dx, c.y + p.dy, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    function drawStar(s: Star) {
      const pulse = 0.4 + Math.sin(time * s.pulseSpeed + s.offset) * 0.6;
      const alpha = Math.max(0, Math.min(1, pulse));
      const halo = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.size * 5);
      halo.addColorStop(0, `rgba(255, 240, 255, ${0.45 * alpha})`);
      halo.addColorStop(1, 'rgba(255, 240, 255, 0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size * 5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawGem(g: Gem) {
      const x = g.baseX + Math.sin(time * g.speed + g.offset) * g.amplitudeX;
      const y = g.baseY + Math.cos(time * g.speed * 0.7 + g.offset) * g.amplitudeY;
      g.rotation += g.rotationSpeed;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(g.rotation);

      // Glow halo
      const halo = ctx.createRadialGradient(0, 0, 0, 0, 0, g.size * 4);
      halo.addColorStop(0, `hsla(${g.hue}, 90%, 75%, 0.55)`);
      halo.addColorStop(1, `hsla(${g.hue}, 90%, 75%, 0)`);
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(0, 0, g.size * 4, 0, Math.PI * 2);
      ctx.fill();

      // Diamond shape
      ctx.fillStyle = `hsla(${g.hue}, 90%, 78%, 0.9)`;
      ctx.beginPath();
      ctx.moveTo(0, -g.size);
      ctx.lineTo(g.size * 0.7, 0);
      ctx.lineTo(0, g.size);
      ctx.lineTo(-g.size * 0.7, 0);
      ctx.closePath();
      ctx.fill();

      // Highlight
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.beginPath();
      ctx.moveTo(0, -g.size);
      ctx.lineTo(g.size * 0.3, -g.size * 0.3);
      ctx.lineTo(-g.size * 0.2, -g.size * 0.2);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }

    function spawnShootingStar() {
      const fromLeft = Math.random() > 0.5;
      const y = Math.random() * canvas.height * 0.35;
      const speed = 12 + Math.random() * 6;
      shootingStars.push({
        x: fromLeft ? -50 : canvas.width + 50,
        y,
        vx: fromLeft ? speed : -speed,
        vy: speed * 0.35,
        length: 80 + Math.random() * 60,
        life: 0,
        maxLife: 90,
      });
    }

    function drawShootingStars() {
      shootingStars.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        s.life += 1;

        const fade = 1 - s.life / s.maxLife;
        const tailX = s.x - (s.vx / Math.hypot(s.vx, s.vy)) * s.length;
        const tailY = s.y - (s.vy / Math.hypot(s.vx, s.vy)) * s.length;

        const grad = ctx.createLinearGradient(s.x, s.y, tailX, tailY);
        grad.addColorStop(0, `rgba(255, 255, 255, ${0.9 * fade})`);
        grad.addColorStop(1, 'rgba(255, 200, 240, 0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();

        // Bright head
        ctx.fillStyle = `rgba(255, 255, 255, ${fade})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
      });

      shootingStars = shootingStars.filter(
        (s) => s.life < s.maxLife && s.x > -100 && s.x < canvas.width + 100
      );
    }

    function renderStatic() {
      drawSky();
      drawRainbowArc();
      clouds.forEach(drawCloud);
      stars.forEach(drawStar);
      gems.forEach(drawGem);
    }

    function animate() {
      if (reducedMotion) return;

      time += 0.01;

      drawSky();
      drawRainbowArc();

      // Drift clouds left → right
      clouds.forEach((c) => {
        c.x += c.speed;
        if (c.x - 80 * c.scale > canvas.width) {
          c.x = -80 * c.scale;
          c.y = canvas.height * (0.05 + Math.random() * 0.35);
        }
        drawCloud(c);
      });

      stars.forEach(drawStar);
      gems.forEach(drawGem);

      if (time > nextShootingStarAt) {
        spawnShootingStar();
        nextShootingStarAt = time + 3 + Math.random() * 5;
      }
      drawShootingStars();

      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) renderStatic();
      else animate();
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) renderStatic();
    else animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ backgroundColor: 'transparent' }}
    />
  );
};

export default RainbowBackground;
